import React, { useState, useRef, useEffect } from 'react';
import { ChatCircle, X, PaperPlaneRight, CircleNotch, Car, ArrowRight, Tag, ArrowLeft, User, Envelope, Phone, MapPin, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatBubble = ({ message, isUser, parts }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`max-w-[85%]`}>
      <div className={`px-4 py-3 text-sm leading-relaxed ${
        isUser 
          ? 'bg-[#E63946] text-white rounded-2xl rounded-br-sm' 
          : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-bl-sm shadow-sm'
      }`}>
        {message.split('\n').map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : ''}>{line}</p>
        ))}
      </div>
      {parts?.length > 0 && (
        <div className="mt-2 space-y-2">
          {parts.map((part, i) => <PartCard key={i} part={part} />)}
        </div>
      )}
    </div>
  </div>
);

const PartCard = ({ part }) => {
  const price = part.ebay_price || part.estimated_price || 0;
  const finalPrice = Math.round(price * 1.2);
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-3 shadow-sm" data-testid="chat-part-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#E63946]/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Car size={16} className="text-[#E63946]" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{part.part_name}</p>
            {part.vehicle && <p className="text-gray-400 text-xs">{part.vehicle}</p>}
            {part.oem_reference && part.oem_reference !== 'À vérifier' && (
              <p className="text-gray-500 text-[10px] font-mono">Ref. {part.oem_reference}</p>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-[#E63946] text-lg">{finalPrice}&nbsp;&euro;</p>
          <p className="text-gray-400 text-[10px]">prix estim&eacute;</p>
        </div>
      </div>
    </div>
  );
};

/* ─── Quote form inside chatbot ─── */
const QuoteForm = ({ parts, vehicleInfo, sessionId, onBack, onSuccess }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/chat/quote`, {
        ...form,
        parts,
        vehicle_info: vehicleInfo,
        session_id: sessionId,
      });
      toast.success('Demande de devis envoyée !');
      onSuccess();
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
    setSubmitting(false);
  };

  const totalPrice = parts.reduce((s, p) => s + Math.round((p.ebay_price || p.estimated_price || 0) * 1.2), 0);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-[#E63946] px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="text-white/80 hover:text-white"><ArrowLeft size={18} /></button>
        <p className="text-white font-semibold text-sm">Demande de devis</p>
      </div>
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* Parts summary */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Pi&egrave;ces s&eacute;lectionn&eacute;es</p>
          {parts.map((p, i) => (
            <div key={i} className="flex justify-between text-sm py-1">
              <span className="text-gray-800 truncate mr-2">{p.part_name}</span>
              <span className="text-[#E63946] font-bold flex-shrink-0">{Math.round((p.ebay_price || p.estimated_price || 0) * 1.2)} &euro;</span>
            </div>
          ))}
          <div className="border-t border-red-200 mt-2 pt-2 flex justify-between">
            <span className="text-sm font-semibold">Total estim&eacute;</span>
            <span className="text-[#E63946] font-bold text-lg">{totalPrice} &euro;</span>
          </div>
        </div>

        {vehicleInfo && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-400">V&eacute;hicule</p>
            <p className="text-sm text-gray-800 font-medium">{vehicleInfo}</p>
          </div>
        )}

        {/* Form fields */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500">Pr&eacute;nom <span className="text-[#E63946]">*</span></label>
            <input type="text" required value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" data-testid="chat-quote-firstname" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Nom <span className="text-[#E63946]">*</span></label>
            <input type="text" required value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" data-testid="chat-quote-lastname" />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Email <span className="text-[#E63946]">*</span></label>
          <input type="email" required value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" data-testid="chat-quote-email" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500">T&eacute;l&eacute;phone <span className="text-[#E63946]">*</span></label>
            <input type="tel" required value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" data-testid="chat-quote-phone" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Ville</label>
            <input type="text" value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" data-testid="chat-quote-city" />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Message (optionnel)</label>
          <textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E63946] resize-none" placeholder="Pr&eacute;cisez vos besoins..." data-testid="chat-quote-message" />
        </div>
      </form>
      <div className="px-4 pb-4 pt-2 flex-shrink-0">
        <button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#E63946] text-white py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#C62828] disabled:opacity-50" data-testid="chat-quote-submit">
          {submitting ? <><CircleNotch size={16} className="animate-spin" /> Envoi...</> : <><PaperPlaneRight size={16} weight="fill" /> Envoyer la demande</>}
        </button>
      </div>
    </div>
  );
};

/* ─── Success screen ─── */
const QuoteSuccess = ({ onReset }) => (
  <div className="flex flex-col h-full">
    <div className="bg-[#E63946] px-4 py-3 flex items-center justify-between flex-shrink-0">
      <p className="text-white font-semibold text-sm">Conseiller Ecocasse</p>
      <button onClick={onReset} className="text-white/60 hover:text-white p-1.5"><X size={20} /></button>
    </div>
    <div className="flex-1 flex items-center justify-center px-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={36} weight="fill" className="text-green-600" />
        </div>
        <h3 className="font-bold text-gray-900 text-lg mb-2">Demande envoy&eacute;e !</h3>
        <p className="text-gray-500 text-sm mb-6">Notre &eacute;quipe vous contactera dans les plus brefs d&eacute;lais.</p>
        <button onClick={onReset} className="bg-[#E63946] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#C62828]">Nouvelle recherche</button>
      </div>
    </div>
  </div>
);

/* ─── Main Chatbot ─── */
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [allParts, setAllParts] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [view, setView] = useState('chat'); // 'chat' | 'quote' | 'success'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (isOpen && inputRef.current && view === 'chat') inputRef.current.focus(); }, [isOpen, view]);

  const openChat = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: "Bonjour ! Je suis votre conseiller pi\u00e8ces Ecocasse. D\u00e9crivez-moi votre probl\u00e8me ou la pi\u00e8ce que vous cherchez, je m'occupe du reste !",
        parts: []
      }]);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text, parts: [] }]);
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/chat`, { session_id: sessionId, message: text });
      const { response, identified_parts, session_id } = resp.data;
      if (!sessionId) setSessionId(session_id);
      if (identified_parts?.length) {
        setAllParts(prev => [...prev, ...identified_parts]);
        // Extract vehicle info from parts
        const v = identified_parts.find(p => p.vehicle);
        if (v?.vehicle) setVehicleInfo(v.vehicle);
      }
      setMessages(prev => [...prev, { role: 'assistant', text: response, parts: identified_parts || [] }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "D\u00e9sol\u00e9, un probl\u00e8me technique. Essayez de reformuler.", parts: [] }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const resetChat = () => {
    setMessages([]); setSessionId(null); setAllParts([]); setVehicleInfo(''); setView('chat'); setIsOpen(false);
  };

  return (
    <>
      {/* Bubble button */}
      {!isOpen && (
        <button onClick={openChat} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#E63946] text-white rounded-full shadow-lg hover:bg-[#C62828] transition-all hover:scale-105 flex items-center justify-center" data-testid="chatbot-open">
          <ChatCircle size={28} weight="fill" />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[400px] h-full sm:h-[600px] sm:max-h-[80vh] flex flex-col bg-[#FAFAFA] sm:rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-testid="chatbot-window">
          
          {view === 'quote' && (
            <QuoteForm
              parts={allParts}
              vehicleInfo={vehicleInfo}
              sessionId={sessionId}
              onBack={() => setView('chat')}
              onSuccess={() => setView('success')}
            />
          )}

          {view === 'success' && <QuoteSuccess onReset={resetChat} />}

          {view === 'chat' && (
            <>
              {/* Header */}
              <div className="bg-[#E63946] px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                    <ChatCircle size={20} weight="fill" className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Conseiller Ecocasse</p>
                    <p className="text-white/60 text-xs">En ligne</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {allParts.length > 0 && (
                    <button onClick={() => setView('quote')} className="text-white/80 hover:text-white text-xs bg-white/10 px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="chatbot-quote">
                      <Tag size={12} />Devis
                    </button>
                  )}
                  <button onClick={resetChat} className="text-white/60 hover:text-white p-1.5" data-testid="chatbot-close">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {messages.map((msg, i) => (
                  <ChatBubble key={i} message={msg.text} isUser={msg.role === 'user'} parts={msg.parts} />
                ))}
                {loading && (
                  <div className="flex justify-start mb-3">
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <CircleNotch size={14} className="animate-spin" />
                        <span className="text-xs">Recherche en cours...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quote CTA */}
              {allParts.length > 0 && (
                <div className="px-4 pb-2 flex-shrink-0">
                  <button onClick={() => setView('quote')} className="w-full bg-[#E63946] text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#C62828] transition-colors" data-testid="chatbot-request-quote">
                    Demander un devis gratuit <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* Input */}
              <div className="px-4 pb-4 pt-2 bg-white border-t border-gray-100 flex-shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={"D\u00e9crivez votre probl\u00e8me ou la pi\u00e8ce recherch\u00e9e..."}
                    rows={1}
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-[#E63946] max-h-20"
                    data-testid="chatbot-input"
                  />
                  <button onClick={sendMessage} disabled={!input.trim() || loading} className="w-10 h-10 bg-[#E63946] text-white rounded-xl flex items-center justify-center hover:bg-[#C62828] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0" data-testid="chatbot-send">
                    <PaperPlaneRight size={18} weight="fill" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
